/**
 * Opportunity Constants
 * Client-side type categories, event types, and funding types
 * Mirrors server-side shared config values
 */

// Opportunity categories for grouped type selection
export const OPPORTUNITY_CATEGORIES = {
    'Work-Based Learning': [
        'Internship',
        'Job Shadow',
        'Apprenticeship',
        'Co-op Program',
        'Job/Employment',
    ],
    'Mentoring & Support': ['Mentorship', 'Coaching Program', 'Peer Mentoring'],
    'Programs & Camps': [
        'Summer Program',
        'After-School Program',
        'Camp',
        'Bootcamp',
        'Cohort Program',
    ],
    'Workshops & Training': [
        'Workshop',
        'Training Session',
        'Certification Course',
        'Professional Development',
        'Webinar',
        'Seminar',
        'Lecture Series',
    ],
    'Networking & Community Events': [
        'Networking Event',
        'Conference',
        'Career Fair',
        'Industry Tour',
        'Site Visit',
        'Open House',
        'Info Session',
    ],
    'Competitions & Showcases': [
        'Competition',
        'Hackathon',
        'Science Fair',
        'Showcase/Expo',
        'Demo Day',
    ],
    'Academic & Research': [
        'Research Experience',
        'Fellowship',
        'Scholarship',
        'Study Abroad',
    ],
    'Funding & Partnerships': [
        'Grant Opportunity',
        'Funding Partnership',
        'Sponsorship',
        'Award/Prize',
    ],
    'Community & Volunteer': [
        'Volunteer',
        'Community Project',
        'Service Learning',
    ],
    Other: ['Resource/Toolkit', 'Other'],
};

// Types that are calendar-worthy events (have specific dates/times)
export const EVENT_TYPES = [
    'Workshop',
    'Training Session',
    'Webinar',
    'Seminar',
    'Lecture Series',
    'Networking Event',
    'Conference',
    'Career Fair',
    'Industry Tour',
    'Site Visit',
    'Open House',
    'Info Session',
    'Competition',
    'Hackathon',
    'Science Fair',
    'Showcase/Expo',
    'Demo Day',
];

// Types that involve funding
export const FUNDING_TYPES = [
    'Grant Opportunity',
    'Funding Partnership',
    'Sponsorship',
    'Award/Prize',
];

// Commitment types
export const COMMITMENT_TYPES = [
    { value: 'one-time', label: 'One-time' },
    { value: 'recurring', label: 'Recurring' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'full-time', label: 'Full-time' },
    { value: 'flexible', label: 'Flexible' },
];

// Application methods
export const APPLICATION_METHODS = [
    { value: 'external_link', label: 'External Link' },
    { value: 'email', label: 'Email' },
    { value: 'in_platform', label: 'In Platform' },
];

// Funding types for the funding step
export const FUNDING_TYPE_OPTIONS = [
    { value: 'grant', label: 'Grant' },
    { value: 'contract', label: 'Contract' },
    { value: 'sponsorship', label: 'Sponsorship' },
    { value: 'donation', label: 'Donation' },
    { value: 'in-kind', label: 'In-Kind' },
    { value: 'matching', label: 'Matching' },
    { value: 'seed', label: 'Seed Funding' },
    { value: 'prize', label: 'Prize' },
    { value: 'other', label: 'Other' },
];

// Award cycle options
export const AWARD_CYCLES = [
    { value: 'one-time', label: 'One-time' },
    { value: 'annual', label: 'Annual' },
    { value: 'biannual', label: 'Biannual' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'rolling', label: 'Rolling' },
];

// Recurrence patterns
export const RECURRENCE_PATTERNS = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Biweekly' },
    { value: 'monthly', label: 'Monthly' },
];

/**
 * Filter the categorized OPPORTUNITY_CATEGORIES grouping down to the
 * tenant's actual allowed opportunity types (`taxonomy.opportunityTypes`,
 * not a fixed platform-wide list).
 *
 * Any tenant-added type that isn't in any known category is bucketed into
 * "Other" rather than dropped, so a custom addition is still selectable.
 * `currentValue` (the opportunity's already-stored type, in edit mode) is
 * always kept present even if it's since fallen outside the tenant's
 * allowed list, so the <select> never renders with no matching option for
 * what's actually selected -- same guarantee the org/opportunity timezone
 * selects rely on.
 *
 * @param {string[]} allowedTypes - The tenant's taxonomy.opportunityTypes.
 * @param {string} [currentValue] - The opportunity's currently selected type.
 * @returns {Record<string, string[]>} Category -> types, filtered.
 */
export function buildOpportunityTypeCategories(allowedTypes, currentValue) {
    // Not loaded yet (or platform mode): show every platform category
    // rather than an empty dropdown during the loading window.
    if (!allowedTypes || allowedTypes.length === 0) {
        return OPPORTUNITY_CATEGORIES;
    }

    const allowedSet = new Set(allowedTypes);
    if (currentValue) allowedSet.add(currentValue);

    const filtered = {};
    for (const [category, types] of Object.entries(OPPORTUNITY_CATEGORIES)) {
        const kept = types.filter((t) => allowedSet.has(t));
        if (kept.length > 0) {
            filtered[category] = kept;
        }
    }

    const categorized = new Set(Object.values(OPPORTUNITY_CATEGORIES).flat());
    const uncategorized = [...allowedSet].filter((t) => !categorized.has(t));
    if (uncategorized.length > 0) {
        filtered.Other = [...(filtered.Other || []), ...uncategorized];
    }

    return filtered;
}

/**
 * Check if an opportunity type is an event type
 */
export function isEventType(type) {
    return EVENT_TYPES.includes(type);
}

/**
 * Check if an opportunity type is a funding type
 */
export function isFundingType(type) {
    return FUNDING_TYPES.includes(type);
}
